import { differenceInCalendarDays } from 'date-fns';
import type { TFunction } from 'i18next';
import { formatLocalDate, toLocalMidnight } from '@/common/utils/dateFormats';
import type { DependencySeverity } from '@/features/item-panel/components/tabs/dependencies/lib/dependencyImpact';
import type { DependencySectionValue } from '@/features/item-panel/components/tabs/dependencies/lib/types';

export interface SectionDescription {
  prefix: string;
  dateText: string | null;
  relativeText: string | null;
  severity: DependencySeverity;
}

export const isDatePast = (date: string | null | undefined): boolean => {
  if (!date) {
    return false;
  }
  const localMidnight = toLocalMidnight(date);
  return !!localMidnight && differenceInCalendarDays(localMidnight, new Date()) < 0;
};

export const buildSectionDescription = (
  section: DependencySectionValue,
  anchorDate: string | null | undefined,
  t: TFunction,
): SectionDescription => {
  const anchorLocalMidnight = anchorDate ? toLocalMidnight(anchorDate) : undefined;

  if (!anchorLocalMidnight) {
    return {
      prefix:
        section === 'predecessors'
          ? t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.predecessorsDescription)
          : t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.successorsDescription),
      dateText: null,
      relativeText: null,
      severity: 'neutral',
    };
  }

  const dateText = formatLocalDate(anchorLocalMidnight);
  const daysUntil = differenceInCalendarDays(anchorLocalMidnight, new Date());
  const isPast = daysUntil < 0;

  const prefix =
    section === 'predecessors'
      ? isPast
        ? t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.waitingOnPastPrefix)
        : t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.waitingOnPrefix)
      : isPast
        ? t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.completionEnablesPastPrefix)
        : t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.completionEnablesPrefix);

  if (daysUntil > 0) {
    return {
      prefix,
      dateText,
      relativeText: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.anchorDateFuture, {
        count: daysUntil,
      }),
      severity: 'neutral',
    };
  }
  if (daysUntil === 0) {
    return {
      prefix,
      dateText,
      relativeText: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.anchorDateToday),
      severity: 'warning',
    };
  }
  return {
    prefix,
    dateText,
    relativeText: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.anchorDatePast, {
      count: Math.abs(daysUntil),
    }),
    severity: 'error',
  };
};
